import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IDType } from "../../../../config/types";
import LeagueSelector from "../../../components/LeagueSelector";
import { translate } from "../../../../config/translator";
import RoundedRectangle from "../../../components/RoundedRectangle";
import MatchTile from "../../../components/Match/MatchTile";
import { ClavaContext } from "../../../../config/contexts";
import { RootState } from "../../../../store";
import { fetchLeagueMatches, fetchSubCategoryMatches } from "../../../../store/actions/matchActions";

type LeagueMatchesViewProps = {
  isVisible: boolean;
  isCup: boolean;
  leagueSelectorItemsWithAll: { key: number | "all"; label: string }[];
  leagueId: IDType;
  subCategoryId?: IDType;
};

const LeagueMatchesView: React.FC<LeagueMatchesViewProps> = ({
                                                               isVisible,
                                                               isCup,
                                                               leagueSelectorItemsWithAll,
                                                               leagueId, subCategoryId
                                                             }) => {
  const { l, aoi } = useContext(ClavaContext);
  const matches = useSelector<RootState, RootState["match"]["leagueMatches"]>(state => state.match.leagueMatches);
  const [selectedLeagueId, setSelectedLeagueId] = useState(leagueId);
  const [isRealCup, setIsRealCup] = useState(isCup);
  const dispatch = useDispatch();
  const key = useMemo(() => {
    if (isRealCup)
      return `${subCategoryId}-${aoi}`;
    return `${selectedLeagueId}`;
  }, [aoi, isRealCup, selectedLeagueId, subCategoryId]);
  const displayMatches = useMemo(() => matches[key] ?? [], [matches, key]);
  const setSubLeague = useCallback((val: string | number | undefined) => {
    if (!val || typeof val === "string") {
      setIsRealCup(true);
      return;
    }
    setSelectedLeagueId(val);
  }, []);
  useEffect(() => {
    if (!matches[key]) {
      if (isRealCup) {
        fetchSubCategoryMatches(dispatch, selectedLeagueId, aoi);
      } else {
        fetchLeagueMatches(dispatch, selectedLeagueId);
      }
    }
  }, [aoi, dispatch, isRealCup, key, matches, selectedLeagueId]);
  if (!isVisible) return null;
  return (
    <div>
      <div className="mb-4 flex flex-col gap-3">
        {isCup && (
          <div className="w-auto min-w-64">
            <LeagueSelector
              value={selectedLeagueId || leagueId}
              onChange={setSubLeague}
              items={leagueSelectorItemsWithAll}
              placeholder={translate("select", l)}
              transparent
              withoutChoose
              containerClassName="relative overflow-hidden h-9 flex-shrink-0 inline-flex items-center justify-start rounded-lg bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pl-3 pr-8"
              className="text-left text-sm"
              dropdownMaxWidth={320}
              dropdownFitContent
            />
          </div>
        )}
      </div>

      <RoundedRectangle
        className="w-full bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4"
        hasBorder
        hasTitle
        titleType="upLeft"
        titleKey="matches"
      >
        <div className="tablet-panel-body mt-3">
          {displayMatches.map((m) => (
            <MatchTile
              key={`league-match-${m.id}`}
              match={m}
              banner="date-league"
            />
          ))}
          {displayMatches.length === 0 && (
            <div className="text-font-600 dark:text-font-600-dark p-4">
              {translate("noMatches", l)}
            </div>
          )}
        </div>
      </RoundedRectangle>
    </div>
  );
};

export default LeagueMatchesView;
